/* A-cart-drawer — fresh Cart A drawer.
 * Keeps the <cart-drawer> element name + public interface used by existing
 * add-to-cart flows: open(), close(), renderContents(parsedState),
 * getSectionsToRender(), #CartDrawer wrapper, data-section-id.
 */
if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId || 'cart-drawer';
        this.bindStaticTriggers();
        this.bindContentTriggers();
      }

      // Triggers that live outside #CartDrawer (persist across refreshes).
      bindStaticTriggers() {
        if (this._staticBound) return;
        this._staticBound = true;

        // Open from the header cart button(s).
        document.querySelectorAll('[data-header-cart-bubble]').forEach((el) => {
          el.setAttribute('role', 'button');
          el.setAttribute('aria-haspopup', 'dialog');
          el.addEventListener('click', (e) => {
            e.preventDefault();
            this.open(el);
          });
        });

        // Close on Escape.
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.classList.contains('active')) this.close();
        });

        // Delegated clicks for overlay / close / steppers / remove.
        this.addEventListener('click', (e) => {
          var swatch = e.target.closest('[data-xsell-opt]');
          if (swatch) { this.onXsellSwatch(swatch); return; }
          if (e.target.closest('[data-cart-overlay]') || e.target.closest('[data-cart-close]')) {
            this.close();
            return;
          }
          var bundleToggle = e.target.closest('[data-bundle-toggle]');
          if (bundleToggle) {
            var bundle = bundleToggle.closest('[data-cart-bundle]');
            if (bundle) bundle.classList.toggle('is-collapsed');
            return;
          }
          var bdToggle = e.target.closest('[data-subtotal-toggle]');
          if (bdToggle) {
            var bd = bdToggle.closest('[data-breakdown]');
            if (bd) {
              var open = bd.classList.toggle('is-open');
              bdToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
            }
            return;
          }
          if (e.target.closest('[data-xsell-prev]')) { this.step(-1); return; }
          if (e.target.closest('[data-xsell-next]')) { this.step(1); return; }
          var addBtn = e.target.closest('[data-xsell-add]');
          if (addBtn) {
            this.addVariant(addBtn.dataset.variantId, addBtn);
            return;
          }
          var bundleRemove = e.target.closest('[data-bundle-remove]');
          if (bundleRemove) {
            var bundleEl = bundleRemove.closest('[data-cart-bundle]');
            if (bundleEl) this.removeBundle(bundleEl);
            return;
          }
          var row = e.target.closest('[data-cart-item]');
          if (!row) return;
          var key = row.dataset.lineKey;
          var qty = parseInt(row.dataset.qty, 10) || 0;
          if (e.target.closest('[data-qty-up]')) this.changeLine(key, qty + 1);
          else if (e.target.closest('[data-qty-down]')) this.changeLine(key, Math.max(0, qty - 1));
          else if (e.target.closest('[data-cart-remove]')) this.changeLine(key, 0);
        });
      }

      // Placeholder for any per-render bindings (kept for parity / future use).
      bindContentTriggers() {}

      open(triggeredBy) {
        if (triggeredBy) this.activeElement = triggeredBy;
        this.classList.add('animate', 'active');
        document.body.style.overflow = 'hidden';
        this.loadRecommendations();
      }

      // Dawn product-form.js / quick-add call this before renderContents()
      // to remember which element to refocus once the drawer opens.
      setActiveElement(element) {
        this.activeElement = element;
      }

      close() {
        this.classList.remove('active');
        document.body.style.overflow = '';
      }

      getSectionsToRender() {
        return [{ id: this.sectionId, selector: '#CartDrawer' }];
      }

      // Pull #CartDrawer innerHTML out of a rendered section HTML string.
      extractInner(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el = doc.querySelector('#CartDrawer');
        return el ? el.innerHTML : null;
      }

      // Called by external add-to-cart code (flow timer): parsedState.sections[id]
      // holds the rendered section HTML.
      renderContents(parsedState) {
        if (parsedState && parsedState.sections && parsedState.sections[this.sectionId]) {
          var inner = this.extractInner(parsedState.sections[this.sectionId]);
          if (inner !== null) this.replaceInner(inner);
        }
        this.open(this.activeElement);
      }

      replaceInner(innerHTML) {
        var target = this.querySelector('#CartDrawer');
        if (!target) return;
        target.innerHTML = innerHTML;
        this.classList.toggle('is-empty', target.dataset.cartCount === '0');
        this.syncHeaderCount(parseInt(target.dataset.cartCount, 10) || 0);
        this.loadRecommendations();
      }

      /* ---------- cross-sell (curated, server-rendered) ---------- */
      loadRecommendations() {
        var container = this.querySelector('[data-xsell]');
        if (!container) return;
        var grid = container.querySelector('[data-xsell-grid]');
        if (!grid) return;
        var count = grid.children.length;
        container.hidden = count === 0;
        var viewport = grid.parentElement;
        var overflow = viewport && viewport.clientWidth > 0 && grid.scrollWidth > viewport.clientWidth + 2;
        var nav = container.querySelector('[data-xsell-nav]');
        if (nav) nav.hidden = !overflow;
        if (overflow) this.setupCarouselLoop(grid, viewport);
        else if (viewport) viewport.scrollLeft = 0;
      }

      // Clone the card set on both sides so touch swiping (and the arrows)
      // wrap around endlessly. When the scroll position drifts into a clone
      // set, we silently reposition by one set width — visually identical, so
      // the loop is seamless in either direction.
      setupCarouselLoop(grid, viewport) {
        if (grid.dataset.loopInit === '1') return;
        if (!viewport.clientWidth) return;
        var reals = Array.prototype.slice.call(grid.children);
        if (!reals.length) return;
        grid.dataset.loopInit = '1';

        function cloneInert(node) {
          var c = node.cloneNode(true);
          c.setAttribute('data-clone', '');
          c.setAttribute('aria-hidden', 'true');
          c.querySelectorAll('button, a, input, [tabindex]').forEach(function (el) {
            el.setAttribute('tabindex', '-1');
          });
          return c;
        }
        // Append a copy of the set, then prepend a copy (kept in original order).
        reals.forEach(function (node) { grid.appendChild(cloneInert(node)); });
        for (var i = reals.length - 1; i >= 0; i--) {
          grid.insertBefore(cloneInert(reals[i]), grid.firstChild);
        }

        var N = reals.length;
        function stride() {
          var gap = parseFloat(getComputedStyle(grid).columnGap) || 8;
          return N * (reals[0].getBoundingClientRect().width + gap);
        }

        // Rest in the middle (real) set.
        viewport.scrollLeft = stride();

        // Reposition only once scrolling settles — never mid-momentum.
        // Jumping during an active flick fights the snap/momentum and makes
        // the track oscillate. The clone sets are identical, so correcting the
        // position at rest is invisible.
        function reposition() {
          var s = stride();
          if (s <= 0) return;
          var x = viewport.scrollLeft;
          if (x < s) viewport.scrollLeft = x + s;
          else if (x >= s * 2) viewport.scrollLeft = x - s;
        }
        var settle;
        viewport.addEventListener(
          'scroll',
          function () {
            clearTimeout(settle);
            settle = setTimeout(reposition, 120);
          },
          { passive: true }
        );
        viewport.addEventListener('scrollend', reposition);
      }

      // Swatch/size chip on a cross-sell card → update price/discount/image/add.
      onXsellSwatch(btn) {
        var card = btn.closest('[data-xsell-card]');
        if (!card) return;
        card.querySelectorAll('[data-xsell-opt]').forEach((b) => b.classList.toggle('is-active', b === btn));
        var ds = btn.dataset;
        var setText = function (s, t) { var el = card.querySelector(s); if (el) el.textContent = t || ''; };
        setText('[data-xsell-price]', ds.price);
        setText('[data-xsell-compare]', ds.compare);
        setText('[data-xsell-pct]', ds.pct);
        var img = card.querySelector('[data-xsell-thumb] img');
        if (img && ds.img) img.src = ds.img;
        var add = card.querySelector('[data-xsell-add]');
        if (add) {
          var soldOut = ds.available !== 'true';
          add.dataset.variantId = ds.variantId;
          add.disabled = soldOut;
          add.textContent = soldOut ? 'Sold out' : 'Add';
        }
      }

      // Carousel: the viewport scroll-snaps (swipable on touch) and the clone
      // sets make it loop. The arrows just scroll by one card; the scroll
      // handler in setupCarouselLoop wraps the ends for both arrows and swipe.
      step(dir) {
        var grid = this.querySelector('[data-xsell-grid]');
        if (!grid || !grid.children.length) return;
        var viewport = grid.parentElement;
        var gap = parseFloat(getComputedStyle(grid).columnGap) || 8;
        var cardW = grid.children[0].getBoundingClientRect().width + gap;
        viewport.scrollBy({ left: dir * cardW, behavior: 'smooth' });
      }

      // Quick-add a recommended product, then refresh the drawer.
      addVariant(variantId, btn) {
        if (!variantId) return;
        if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: [this.sectionId] }),
        })
          .then((r) => r.json())
          .then((state) => {
            if (state && state.sections && state.sections[this.sectionId]) {
              var doc = new DOMParser().parseFromString(state.sections[this.sectionId], 'text/html');
              var newCD = doc.querySelector('#CartDrawer');
              var cur = this.querySelector('#CartDrawer');
              if (newCD && cur) {
                cur.innerHTML = newCD.innerHTML;
                cur.dataset.cartCount = newCD.dataset.cartCount;
                this.classList.toggle('is-empty', newCD.dataset.cartCount === '0');
                this.syncHeaderCount(parseInt(newCD.dataset.cartCount, 10) || 0);
              }
            }
            this.loadRecommendations();
            this.open();
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: state }));
          })
          .catch((err) => console.error('Add failed:', err));
      }

      syncHeaderCount(count) {
        document.querySelectorAll('.mdseh__cartcount, .mdseh__mcount').forEach((el) => {
          el.textContent = '(' + count + ')';
        });
        document.querySelectorAll('[data-bubble-count]').forEach((el) => {
          el.textContent = count;
        });
      }

      // Remove every line item belonging to a bundle in one request.
      // Shows a "Removing…" pulse on the tile until the cart re-renders.
      removeBundle(bundleEl) {
        if (!bundleEl || bundleEl.classList.contains('is-deleting')) return;
        var keys = [];
        bundleEl.querySelectorAll('[data-cart-item]').forEach(function (row) {
          if (row.dataset.lineKey) keys.push(row.dataset.lineKey);
        });
        if (!keys.length) return;

        bundleEl.classList.add('is-deleting');
        var nameEl = bundleEl.querySelector('[data-bundle-toggle]');
        if (nameEl) nameEl.textContent = 'Removing…';

        var updates = {};
        keys.forEach(function (k) { updates[k] = 0; });

        fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates, sections: [this.sectionId] }),
        })
          .then((r) => r.json())
          .then((state) => {
            if (state && state.sections && state.sections[this.sectionId]) {
              var inner = this.extractInner(state.sections[this.sectionId]);
              if (inner !== null) {
                this.querySelector('#CartDrawer').innerHTML = inner;
                this.classList.toggle('is-empty', state.item_count === 0);
              }
            }
            if (typeof state.item_count === 'number') this.syncHeaderCount(state.item_count);
            this.loadRecommendations();
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: state }));
          })
          .catch((err) => {
            console.error('Bundle remove failed:', err);
            bundleEl.classList.remove('is-deleting');
          });
      }

      // Quantity change / remove via the AJAX Cart API + Section Rendering.
      changeLine(key, quantity) {
        if (!key) return;
        fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity, sections: [this.sectionId] }),
        })
          .then((r) => r.json())
          .then((state) => {
            if (state && state.sections && state.sections[this.sectionId]) {
              var inner = this.extractInner(state.sections[this.sectionId]);
              if (inner !== null) {
                this.querySelector('#CartDrawer').innerHTML = inner;
                this.classList.toggle('is-empty', state.item_count === 0);
              }
            }
            if (typeof state.item_count === 'number') this.syncHeaderCount(state.item_count);
            this.loadRecommendations();
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: state }));
          })
          .catch((err) => console.error('Cart change failed:', err));
      }
    }
  );
}
